import { api } from './client';

function getToken(): string {
  // Mirror client.ts: prefer alpha_token, fall back to legacy 'token'.
  // clearToken/setToken in client.ts wipe both keys to keep them in sync.
  return localStorage.getItem('alpha_token') || localStorage.getItem('token') || '';
}

export interface ChatMessage { role: string; content: string; }

// ─── Chat Conversation Types ─────────────────────────────────────────────────

export interface ChatConversation {
  id: number;
  userId: number;
  title: string;
  createdAt: number;
  updatedAt: number;
}

export interface ChatMessageRecord {
  id: number;
  conversationId: number;
  userId: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: number;
}

// ─── Conversation CRUD ───────────────────────────────────────────────────────

export const listChatConversations = () =>
  api.get<{ data: ChatConversation[] }>('/alpha-lab/chat-conversations');

export const createChatConversation = (title?: string) =>
  api.post<{ data: ChatConversation }>('/alpha-lab/chat-conversations', { title });

export const renameChatConversation = (id: number, title: string) =>
  api.patch(`/alpha-lab/chat-conversations/${id}`, { title });

export const deleteChatConversation = (id: number) =>
  api.delete(`/alpha-lab/chat-conversations/${id}`);

export const getChatMessages = (conversationId: number) =>
  api.get<{ data: ChatMessageRecord[] }>(`/alpha-lab/chat-conversations/${conversationId}/messages`);

// ─── Memory Management ─────────────────────────────────────────────────────

export interface UserMemory {
  id: number;
  userId: number;
  category: string;
  content: string;
  source: string;
  confidence: number;
  sourceConversationId: number;
  lastMentionedAt: number;
  createdAt: number;
}

export const listMemories = (category?: string, limit = 50) => {
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  params.set('limit', String(limit));
  return api.get<{ data: UserMemory[]; total: number }>(`/alpha-lab/memories?${params.toString()}`);
};

export const getMemory = (id: number) =>
  api.get<{ data: UserMemory }>(`/alpha-lab/memories/${id}`);

export const createMemory = (category: string, content: string) =>
  api.post<{ data: UserMemory }>('/alpha-lab/memories', { category, content });

export const updateMemory = (id: number, updates: { category?: string; content?: string }) =>
  api.patch<{ data: UserMemory }>(`/alpha-lab/memories/${id}`, updates);

export const deleteMemory = (id: number) =>
  api.delete(`/alpha-lab/memories/${id}`);

export const batchDeleteMemories = (ids: number[]) =>
  api.post('/alpha-lab/memories/batch-delete', { ids });

export const getMemoryCategories = () =>
  api.get<{ data: { id: string; label: string; icon: string }[] }>('/alpha-lab/memories/categories');

// Check if free user can ask about a stock (must be in holdings or watchlist)
export const checkStockAccess = (code: string, name?: string) =>
  api.post<{ allowed: boolean; reason?: string }>('/alpha-lab/chat/check-stock', { code, name });

// Re-export the intent type for consumers
import type { ChatIntent } from "../components/feature/ActionButtons";
export type { ChatIntent };

export interface SendChatOptions {
  signal?: AbortSignal;
  /** Called when the server pushes a structured intent frame. */
  onIntent?: (intent: ChatIntent) => void;
}

export async function* sendChatMessage(
  messages: ChatMessage[],
  sessionId: number | null,
  conversationId?: number,
  options?: SendChatOptions | AbortSignal,
): AsyncGenerator<string> {
  const token = getToken();
  // Accept both new options-object and legacy AbortSignal for back-compat.
  let signal: AbortSignal | undefined;
  let onIntent: ((intent: ChatIntent) => void) | undefined;
  if (options && 'signal' in options) {
    signal = options.signal;
    onIntent = options.onIntent;
  } else if (options instanceof AbortSignal || options === undefined) {
    signal = options;
  } else if (options && 'aborted' in options) {
    // duck-type AbortSignal
    signal = options as AbortSignal;
  }
  // sessionId goes in the BODY, not an X-Alpha-Session header. A custom
  // header triggers a CORS preflight that webviews cache for up to 12h
  // (Access-Control-Max-Age); a stale "disallowed" preflight kept the chat
  // POST blocked in the APK even after the server whitelisted the header.
  // Sending only already-allowed headers (Authorization/Accept/Content-Type)
  // sidesteps that trap. Backend reads sessionId from body (header kept as
  // fallback for older web clients).
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch('/api/alpha-lab/chat', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      messages,
      stream: true,
      ...(sessionId != null ? { sessionId } : {}),
      ...(conversationId ? { conversationId } : {}),
    }),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`HTTP ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';

  // Wrap the read loop in try/finally so the reader is always cancelled
  // even if the consumer breaks out early — otherwise the fetch keeps
  // draining bytes from upstream after the user navigated away.
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });

      // Process SSE lines
      const lines = buf.split('\n');
      buf = lines.pop() ?? '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || !trimmed.startsWith('data:')) continue;
        const data = trimmed.slice(5).trim();
        if (data === '[DONE]') return;
        try {
          const parsed = JSON.parse(data);
          // Structured intent frame (server-side rule engine)
          if (parsed?.type === 'intent' && parsed?.payload && onIntent) {
            onIntent(parsed.payload as ChatIntent);
            continue;
          }
          // Standard OpenAI-style delta
          const delta = parsed?.choices?.[0]?.delta?.content;
          if (delta) yield delta;
        } catch {
          // Ignore malformed SSE frames
        }
      }
    }
  } finally {
    reader.cancel().catch(() => {});
  }
}
